import { useCallback, useEffect, useState } from "react";
import {
  getAdminCommunitySubmissions,
  postAdminCommunityCourseAction,
  postAdminCommunityLessonAction,
  type CommunitySubmissionRow,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

export default function AdminCommunityReviewPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [submissions, setSubmissions] = useState<CommunitySubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminCommunitySubmissions(bearer);
      setSubmissions(data.submissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load review queue");
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleApprove(row: CommunitySubmissionRow) {
    if (!bearer || busyId) return;
    setBusyId(row.id);
    setError(null);
    try {
      if (row.kind === "lesson") {
        await postAdminCommunityLessonAction(bearer, row.id, "approve");
      } else {
        await postAdminCommunityCourseAction(bearer, row.id, "approve");
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(row: CommunitySubmissionRow) {
    if (!bearer || busyId) return;
    const reason = window.prompt("Rejection reason for the teacher:");
    if (!reason?.trim()) return;
    setBusyId(row.id);
    setError(null);
    try {
      if (row.kind === "lesson") {
        await postAdminCommunityLessonAction(bearer, row.id, "reject", { reason: reason.trim() });
      } else {
        await postAdminCommunityCourseAction(bearer, row.id, "reject", { reason: reason.trim() });
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reject failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card card-wide">
      <header className="row-header">
        <div>
          <h1>Community review</h1>
          <p className="lede">Approve or reject lessons and courses submitted to the community catalog.</p>
        </div>
      </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="muted">Loading queue…</p>
        ) : submissions.length === 0 ? (
          <p className="muted">No pending submissions.</p>
        ) : (
          <table className="teacher-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Owner</th>
                <th>Submitted</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {submissions.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <td>{row.kind}</td>
                  <td>
                    <strong>{row.title}</strong>
                    {row.description ? <p className="muted small">{row.description}</p> : null}
                  </td>
                  <td>{row.ownerEmail}</td>
                  <td>{row.submittedAt ? new Date(row.submittedAt).toLocaleString() : "—"}</td>
                  <td className="teacher-table-actions">
                    <button
                      type="button"
                      className="btn primary tight"
                      disabled={busyId !== null}
                      onClick={() => void handleApprove(row)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn secondary tight"
                      disabled={busyId !== null}
                      onClick={() => void handleReject(row)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}
